import { css, html, LitEntity, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-banner';
import '../../common/bn-surface';
import '../../common/bn-toggle';
import './bin-height-map';
import './bin-level-amplitude-image';

class BinHeightMapInspector extends LitEntity {
  constructor() {
    super();
  }

  static get properties() {
    return {
      binLevelId: { type: String, attribute: 'bin-level-id' },
      renderer: { type: Object, attribute: false },
      role: { type: String, attribute: false },
      showAmplitudeImage: { type: Boolean, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: flex;
        gap: 1em;
        align-items: stretch;
        overflow: hidden;
        height: 100%;
      }
      bin-height-map {
        flex-grow: 1;
        width: 1px;
        align-self: stretch;
      }
      .control-box {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        width: 320px;
        overflow: auto;
      }
      .amplitude-image {
        border: 1px solid var(--gray-40);
        background: var(--gray-20);
      }

      bn-surface {
        flex-grow: 1;
        align-self: stretch;
        margin: 0;
        display: flex;
      }

      @media(max-width: 620px) {
        .control-box {
          display: none;
        }
      }
    `];
  }

  renderEntity() {
    return html`
      <bn-surface>
        <bin-height-map
            .href="${this.href}"
            user-control
            bin-level-id="${this.binLevelId || ''}"
            @renderer-mounted="${(e) => this.renderer = e.detail.renderer}"
            ?hide-legs="${this.role === 'ingredient'}"
        >
          <p class="subdued">
            No height map information at the time selected.
          </p>
        </bin-height-map>
      </bn-surface>

      ${this._renderControls()}
    `;
  }

  _renderControls() {
    const showImage = this.showAmplitudeImage && this.role === 'ingredient';
    const disabled = !this.renderer;
    return html`
      <div class="control-box">
        <h3>Controls</h3>

        <div class="controls">
          <div class="form-field">
            <label>
              Cross Section
              <input type="range" value="100" @input="${this._onChangeRange}" />
            </label>
          </div>

          <bn-toggle
            name="Auto Rotate"
            ?disabled="${disabled}"
            ?checked="${this.renderer?.autoRotate}"
            @update="${({ detail: { value } }) => this.renderer.autoRotate = value}"
          ></bn-toggle>

          <bn-toggle
            name="Show Bin"
            ?disabled="${disabled}"
            ?checked="${!this.renderer?.hideBin}"
            @update="${this._onToggleShowBin}"
          ></bn-toggle>

          <bn-toggle
            name="Show Rings"
            ?disabled="${disabled || this.renderer.hideBin}"
            ?checked="${!this.renderer?.hideRings}"
            @update="${({ detail: { value } }) => this.renderer.hideRings = !value}"
          ></bn-toggle>

          <bn-toggle
            name="Show Legs"
            ?disabled="${disabled || this.renderer.hideBin}"
            ?checked="${!this.renderer?.hideLegs}"
            @update="${({ detail: { value } }) => this.renderer.hideLegs = !value}"
          ></bn-toggle>

          <bn-toggle
            name="Solid Walls"
            ?disabled="${disabled || this.renderer.hideBin}"
            ?checked="${this.renderer?.solidBinWalls}"
            @update="${({ detail: { value } }) => this.renderer.solidBinWalls = value}"
          ></bn-toggle>

          ${renderIf(this.role === 'ingredient', () => html`
            <bn-toggle
              name="Show Amplitude Image"
              ?checked="${this.showAmplitudeImage}"
              @update="${({ detail: { value } }) => this.showAmplitudeImage = value}"
            ></bn-toggle>
          `)}
        </div>

        ${renderIf(showImage, () => html`
          <div class="amplitude-image">
            <bin-level-amplitude-image .href=${this.binLevelId}></bin-level-amplitude-image>
          </div>
        `)}
      </div>
    `;
  }

  async _entityChanged(entity) {
    super._entityChanged(entity);
    this.role = this.entity?.properties.role;
  }

  _onToggleShowBin({ detail: { value } }) {
    this.renderer.hideBin = !value;
    this.requestUpdate();
  }

  _onChangeRange(e) {
    this.renderer.clipPosition = -1.5 + (3 * (e.target.value / 100));
  }
}

window.customElements.define('bin-height-map-inspector', BinHeightMapInspector);
